import React from 'react'
import { PiEnvelope, PiHourglass, PiListChecks } from 'react-icons/pi'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import type { ActionSummaryStructuredData } from '../../services/action_summary_api'

interface SummaryStatsHeaderProps {
  data: ActionSummaryStructuredData
  className?: string
}

interface StatCardProps {
  icon: React.ReactNode
  value: number | string
  label: string
  variant: 'danger' | 'warning' | 'info' | 'neutral'
  tooltipTitle: string
  tooltipContent: string
}

const variantStyles = {
  danger: 'bg-gray-50 border-gray-200 text-gray-700',
  warning: 'bg-gray-50 border-gray-200 text-gray-700',
  info: 'bg-gray-50 border-gray-200 text-gray-700',
  neutral: 'bg-gray-50 border-gray-200 text-gray-700',
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  variant,
  tooltipTitle,
  tooltipContent,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-help',
        variantStyles[variant]
      )}
    >
      <div className="flex-shrink-0" style={{ color: '#97b98f' }}>
        {icon}
      </div>
      <div>
        <div className="text-lg font-bold leading-tight">{value}</div>
        <div className="text-xs opacity-80 leading-tight">{label}</div>
      </div>
    </div>
  </Tooltip>
)

/**
 * Summary stats header showing signal-based counts.
 *
 * Displays:
 * - Unreplied emails count (3-60 days old)
 * - Inactive deals count (7-90 days stale or 14+ days in stage)
 * - Total actions count
 */
export const SummaryStatsHeader: React.FC<SummaryStatsHeaderProps> = ({
  data,
  className,
}) => {
  const unrepliedEmails =
    data.signal_counts?.unreplied_email ??
    data.actions_by_signal?.unreplied_email?.length ??
    data.emails_needing_reply?.length ??
    0
  const inactiveDeals =
    data.signal_counts?.inactive_deal ??
    data.actions_by_signal?.inactive_deal?.length ??
    data.inactive_deals?.length ??
    0
  const totalSignals =
    data.signal_counts?.total ?? unrepliedEmails + inactiveDeals

  const actionsCount = data.enriched_actions?.length ?? 0

  // Check if we're showing a subset of signals
  const hasMoreSignals = totalSignals > actionsCount && actionsCount > 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="grid grid-cols-3 gap-2">
        <StatCard
          icon={<PiEnvelope size={20} />}
          value={unrepliedEmails}
          label="Unreplied"
          variant={unrepliedEmails > 0 ? 'danger' : 'neutral'}
          tooltipTitle="Unreplied Emails"
          tooltipContent="Inbound emails from 3-60 days ago that need a response. Automated emails (shipping, out-of-office) are filtered out."
        />
        <StatCard
          icon={<PiHourglass size={20} />}
          value={inactiveDeals}
          label="Inactive Deals"
          variant={inactiveDeals > 0 ? 'warning' : 'neutral'}
          tooltipTitle="Inactive Deals"
          tooltipContent="Deals needing attention: 7+ days with no activity OR 14+ days stuck in the same stage. Limited to deals active within the last 90 days."
        />
        <StatCard
          icon={<PiListChecks size={20} />}
          value={actionsCount}
          label="Actions"
          variant="neutral"
          tooltipTitle="Recommended Actions"
          tooltipContent={`AI-generated actions for the top ${actionsCount} of ${totalSignals} signals.`}
        />
      </div>
      {hasMoreSignals && (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-1.5">
          <span className="font-medium text-gray-600">
            Showing top {actionsCount} of {totalSignals} signals
          </span>
          <span className="text-gray-400">•</span>
          <span>Prioritized by urgency (unreplied emails first)</span>
        </div>
      )}
    </div>
  )
}
